import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Archive,
  BadgeCheck,
  Mail,
  Phone,
  Plus,
  Search,
  Settings2,
  ShieldCheck,
  UserCheck,
  UsersRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Modal } from '../components/Modal';
import { GuidedEmpty } from '../components/GuidedEmpty';
import { AdminModulePage, ConfirmDialog, PageHero } from '../components/PageTemplates';
import { paths } from '../lib/paths';
import { useStore } from '../hooks/useStore';
import {
  archiveProfile,
  createProfile,
  deactivateProfile,
  findDuplicateProfileCandidates,
  getContactReadiness,
  normalizeProfile,
  reactivateProfile,
  type UpsertProfileInput,
} from '../services/directoryService';
import { updateLeague } from '../services/leagueService';
import {
  DEFAULT_DIRECTORY_FIELDS,
  ROLE_LABEL,
  type DirectoryStatus,
  type LeagueDirectoryField,
  type Profile,
  type Role,
} from '../models';

const ALL_ROLES: Role[] = ['admin', 'scorekeeper', 'referee', 'captain', 'player', 'fan', 'staff'];
const CORE_FIELDS = ['Name', 'Email', 'Phone', 'Roles', 'Access', 'Preferred contact', 'Text consent', 'Marketing consent', 'Status'];
const DEFAULT_ATTRIBUTE_KEYS = new Set(DEFAULT_DIRECTORY_FIELDS.map((field) => field.key));
type ReadinessFilter = 'all' | 'ready' | 'warning' | 'blocked';

export function DirectoryPage() {
  const db = useStore((d) => d);
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<Role | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<DirectoryStatus | 'all'>('all');
  const [readinessFilter, setReadinessFilter] = useState<ReadinessFilter>('all');
  const [archiveProfileId, setArchiveProfileId] = useState('');

  const profiles = useMemo(
    () => [...db.profiles].map(normalizeProfile).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [db.profiles],
  );
  const tableFields = useMemo(
    () => (db.league.directoryFields ?? []).filter((field) => field.showInTable).slice(0, 3),
    [db.league.directoryFields],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return profiles.filter((profile) => {
      const readiness = getContactReadiness(profile);
      const haystack = [
        profile.displayName,
        profile.email,
        profile.phone,
        profile.directoryStatus,
        ...profile.globalRoles.map((role) => ROLE_LABEL[role]),
        ...Object.values(profile.attributes ?? {}).map(String),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (q && !haystack.includes(q)) return false;
      if (roleFilter !== 'all' && !profile.globalRoles.includes(roleFilter)) return false;
      if (statusFilter !== 'all' && profile.directoryStatus !== statusFilter) return false;
      if (readinessFilter !== 'all' && readiness.status !== readinessFilter) return false;
      return true;
    });
  }, [profiles, query, readinessFilter, roleFilter, statusFilter]);

  const metrics = useMemo(() => {
    const counts = Object.fromEntries(ALL_ROLES.map((role) => [role, 0])) as Record<Role, number>;
    for (const profile of profiles) {
      if (profile.directoryStatus === 'archived') continue;
      for (const role of profile.globalRoles) counts[role] += 1;
    }
    return {
      total: profiles.filter((profile) => profile.directoryStatus !== 'archived').length,
      archived: profiles.filter((profile) => profile.directoryStatus === 'archived').length,
      unreachable: profiles.filter((profile) => profile.directoryStatus !== 'archived' && getContactReadiness(profile).status === 'blocked').length,
      duplicates: profiles.reduce((sum, profile) => sum + (findDuplicateProfileCandidates(profile, db).length > 0 ? 1 : 0), 0),
      roles: counts,
    };
  }, [db, profiles]);

  return (
    <AdminModulePage>
      <PageHero
        title="How Directory Works"
        description="Directory holds players, captains, admins, scorekeepers, referees, staff, and anyone who registers. Email is the identity key for sign-in, access, communication delivery, rosters, registration, and event assignments."
        icon={UsersRound}
        accent="arc"
      />

      <div className="directory-metric-strip">
        <CompactMetric icon={UsersRound} label="Total" value={metrics.total} />
        <CompactMetric icon={ShieldCheck} label="Admins" value={metrics.roles.admin} />
        <CompactMetric icon={BadgeCheck} label="Scorekeepers" value={metrics.roles.scorekeeper} />
        <CompactMetric icon={UserCheck} label="Captains" value={metrics.roles.captain} />
        <CompactMetric icon={UsersRound} label="Players" value={metrics.roles.player} />
        <CompactMetric icon={BadgeCheck} label="Referees" value={metrics.roles.referee} />
        <CompactMetric icon={Mail} label="Unreachable" value={metrics.unreachable} tone={metrics.unreachable > 0 ? 'warn' : undefined} />
      </div>

      <section className="card">
        <div className="directory-toolbar directory-toolbar--table">
          <div className="directory-search">
            <Search size={14} aria-hidden />
            <input
              className="input"
              placeholder="Search Directory"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Search directory"
            />
          </div>
          <div className="directory-filter-actions">
            <FilterSelect
              label="Role"
              value={roleFilter}
              onChange={(value) => setRoleFilter(value as Role | 'all')}
              options={[
                ['all', 'All roles'],
                ...ALL_ROLES.map((role) => [role, ROLE_LABEL[role]] as [string, string]),
              ]}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={(value) => setStatusFilter(value as DirectoryStatus | 'all')}
              options={[
                ['all', 'All statuses'],
                ['active', 'Active'],
                ['inactive', 'Inactive'],
                ['archived', 'Archived'],
              ]}
            />
            <FilterSelect
              label="Readiness"
              value={readinessFilter}
              onChange={(value) => setReadinessFilter(value as ReadinessFilter)}
              options={[
                ['all', 'All readiness'],
                ['ready', 'Ready'],
                ['warning', 'Needs attention'],
                ['blocked', 'Unreachable'],
              ]}
            />
            <button className="chip-btn" onClick={() => setFieldsOpen(true)}>
              <Settings2 size={14} /> Manage fields
            </button>
            <button className="chip-btn chip-btn--primary" onClick={() => setAddOpen(true)}>
              <Plus size={14} /> Add person
            </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <GuidedEmpty
            icon={UsersRound}
            title={profiles.length === 0 ? 'Start with the league directory' : 'No people match this view'}
            body={profiles.length === 0
              ? 'Add the first person to start connecting registration, rosters, assignments, communication, and sign-in identity.'
              : 'Adjust the filters or add the person so they can be rostered, assigned, and messaged.'}
          />
        ) : (
          <>
            <div className="table-scroll directory-table-scroll">
              <table className="table table--clickable directory-table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Email / OAuth</th>
                    <th>Roles</th>
                    <th>Access</th>
                    <th>Contact</th>
                    {tableFields.map((field) => <th key={field.key}>{field.label}</th>)}
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((profile) => (
                    <PersonRow
                      key={profile.id}
                      profile={profile}
                      fields={tableFields}
                      onOpen={() => navigate(paths.adminDirectoryProfile(profile.id))}
                      onArchive={() => setArchiveProfileId(profile.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="directory-mobile-list">
              {filtered.map((profile) => (
                <PersonCard
                  key={profile.id}
                  profile={profile}
                  fields={tableFields}
                  onOpen={() => navigate(paths.adminDirectoryProfile(profile.id))}
                  onArchive={() => setArchiveProfileId(profile.id)}
                />
              ))}
            </div>
          </>
        )}
      </section>

      <ProfileModal open={addOpen} onClose={() => setAddOpen(false)} />
      <FieldManagerModal open={fieldsOpen} onClose={() => setFieldsOpen(false)} />
      <ConfirmDialog
        open={Boolean(archiveProfileId)}
        title={`Archive ${profiles.find((profile) => profile.id === archiveProfileId)?.displayName ?? 'this person'}?`}
        body="Historical records will stay attached, but this person will be removed from active Directory views."
        confirmLabel="Archive person"
        tone="danger"
        onCancel={() => setArchiveProfileId('')}
        onConfirm={() => {
          if (archiveProfileId) archiveProfile(archiveProfileId);
          setArchiveProfileId('');
        }}
      />
    </AdminModulePage>
  );
}

function PersonCard({ profile, fields, onArchive, onOpen }: { profile: Profile; fields: LeagueDirectoryField[]; onArchive: () => void; onOpen: () => void }) {
  const readiness = getContactReadiness(profile);
  const isArchived = profile.directoryStatus === 'archived';
  const access = getAccessLabel(profile);
  return (
    <article className="directory-person-card">
      <button type="button" className="directory-person-card__main" onClick={onOpen}>
        <div className="avatar" aria-hidden>{initials(profile.displayName)}</div>
        <div>
          <strong>{profile.displayName}</strong>
          {profile.email ? (
            <span className="directory-inline"><Mail size={12} /> {profile.email}</span>
          ) : (
            <span className="chip chip--warn">Missing email</span>
          )}
          {profile.phone && <span className="directory-inline"><Phone size={12} /> {profile.phone}</span>}
        </div>
      </button>
      <div className="directory-chip-row">
        {profile.globalRoles.map((role) => <span key={role} className="chip">{ROLE_LABEL[role]}</span>)}
      </div>
      <div className="directory-card-grid">
        <span className={`chip chip--${access.tone}`}>{access.label}</span>
        <span className={`chip chip--${readiness.status === 'ready' ? 'ok' : readiness.status === 'warning' ? 'warn' : 'err'}`}>{readiness.label}</span>
        <span className="chip">{profile.directoryStatus}</span>
      </div>
      {fields.length > 0 && (
        <div className="directory-card-fields">
          {fields.map((field) => (
            <div key={field.key}>
              <span className="field__label">{field.label}</span>
              <strong>{formatAttribute(profile.attributes?.[field.key])}</strong>
            </div>
          ))}
        </div>
      )}
      <div className="directory-card-actions">
        <button className="btn btn--ghost btn--sm" onClick={onOpen}>Edit</button>
        {isArchived ? (
          <button className="btn btn--ghost btn--sm" onClick={() => reactivateProfile(profile.id)}>Reactivate</button>
        ) : (
          <>
            <button className="btn btn--ghost btn--sm" onClick={() => deactivateProfile(profile.id)}>Deactivate</button>
            <button
              className="btn btn--ghost btn--sm"
              onClick={onArchive}
            >
              <Archive size={12} /> Archive
            </button>
          </>
        )}
      </div>
    </article>
  );
}

function PersonRow({ profile, fields, onArchive, onOpen }: { profile: Profile; fields: LeagueDirectoryField[]; onArchive: () => void; onOpen: () => void }) {
  const readiness = getContactReadiness(profile);
  const isArchived = profile.directoryStatus === 'archived';
  const access = getAccessLabel(profile);
  return (
    <tr
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      aria-label={`Open ${profile.displayName}`}
    >
      <td>
        <div className="directory-person-cell">
          <div className="avatar" aria-hidden>{initials(profile.displayName)}</div>
          <div>
            <Link
              to={paths.adminDirectoryProfile(profile.id)}
              className="directory-row__name"
              onClick={(event) => event.stopPropagation()}
            >
              {profile.displayName}
            </Link>
            {profile.phone && <div className="muted directory-inline"><Phone size={12} /> {profile.phone}</div>}
          </div>
        </div>
      </td>
      <td>
        {profile.email ? (
          <span className="directory-inline"><Mail size={12} /> {profile.email}</span>
        ) : (
          <span className="chip chip--warn">Missing email</span>
        )}
      </td>
      <td>
        <div className="directory-chip-row">
          {profile.globalRoles.map((role) => <span key={role} className="chip">{ROLE_LABEL[role]}</span>)}
        </div>
      </td>
      <td><span className={`chip chip--${access.tone}`}>{access.label}</span></td>
      <td><span className={`chip chip--${readiness.status === 'ready' ? 'ok' : readiness.status === 'warning' ? 'warn' : 'err'}`}>{readiness.label}</span></td>
      {fields.map((field) => (
        <td key={field.key} className="muted">{formatAttribute(profile.attributes?.[field.key])}</td>
      ))}
      <td><span className="chip">{profile.directoryStatus}</span></td>
      <td onClick={(event) => event.stopPropagation()}>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn--ghost btn--sm" onClick={onOpen}>Edit</button>
          {isArchived ? (
            <button className="btn btn--ghost btn--sm" onClick={() => reactivateProfile(profile.id)}>Reactivate</button>
          ) : (
            <>
              <button className="btn btn--ghost btn--sm" onClick={() => deactivateProfile(profile.id)}>Deactivate</button>
              <button
                className="btn btn--ghost btn--sm"
                onClick={onArchive}
              >
                <Archive size={12} /> Archive
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function FieldManagerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const league = useStore((db) => db.league);
  const fields = league.directoryFields ?? [];
  const defaultFields = fields.filter((field) => DEFAULT_ATTRIBUTE_KEYS.has(field.key));
  const customFields = fields.filter((field) => !DEFAULT_ATTRIBUTE_KEYS.has(field.key));
  const [draft, setDraft] = useState({
    label: '',
    key: '',
    type: 'text' as LeagueDirectoryField['type'],
    options: '',
    showInTable: false,
    registrationField: true,
  });
  const derivedKey = normalizeFieldKey(draft.key || draft.label);
  const keyTaken = Boolean(derivedKey && fields.some((field) => field.key === derivedKey));

  useEffect(() => {
    if (open) setDraft({ label: '', key: '', type: 'text', options: '', showInTable: false, registrationField: true });
  }, [open]);

  const updateField = (key: string, patch: Partial<LeagueDirectoryField>) => {
    updateLeague({
      directoryFields: fields.map((field) => field.key === key ? { ...field, ...patch } : field),
    });
  };
  const removeField = (key: string) => {
    updateLeague({ directoryFields: fields.filter((field) => field.key !== key) });
  };
  const addField = () => {
    const key = derivedKey;
    const label = draft.label.trim();
    if (!key || !label || fields.some((field) => field.key === key)) return;
    updateLeague({
      directoryFields: [
        ...fields,
        {
          id: `field-${key}`,
          key,
          label,
          type: draft.type,
          options: draft.type === 'select' ? splitCsv(draft.options) : [],
          showInTable: draft.showInTable,
          registrationField: draft.registrationField,
        },
      ],
    });
    setDraft({ label: '', key: '', type: 'text', options: '', showInTable: false, registrationField: true });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Manage Directory fields"
      subtitle="Default identity fields are always available. Add custom profile fields when your league needs more information."
      size="lg"
      footer={<button className="btn btn--primary" onClick={onClose}>Done</button>}
    >
      <div className="directory-field-manager">
        <section className="directory-field-panel">
          <div>
            <h3 className="card__title">Core identity fields</h3>
            <p className="card__sub">These fields power sign-in, access, communication, and profile health.</p>
          </div>
          <div className="directory-core-field-list">
            {CORE_FIELDS.map((field) => (
              <div key={field} className="directory-core-field">
                <span>{field}</span>
                <span className="chip">Locked</span>
              </div>
            ))}
          </div>
        </section>

        <section className="directory-field-panel">
          <div>
            <h3 className="card__title">Default profile fields</h3>
            <p className="card__sub">Built-in attributes that can appear in the table and registration flows.</p>
          </div>
          <div className="directory-managed-field-list">
            {defaultFields.map((field) => (
              <ManagedFieldRow
                key={field.key}
                field={field}
                builtIn
                onChange={(patch) => updateField(field.key, patch)}
              />
            ))}
          </div>
        </section>

        <section className="directory-field-panel">
          <div>
            <h3 className="card__title">Custom fields</h3>
            <p className="card__sub">League-specific data points for registration, profiles, search, and communication segments.</p>
          </div>
          {customFields.length === 0 ? (
            <div className="empty">No custom fields yet.</div>
          ) : (
            <div className="directory-managed-field-list">
              {customFields.map((field) => (
                <ManagedFieldRow
                  key={field.key}
                  field={field}
                  onChange={(patch) => updateField(field.key, patch)}
                  onRemove={() => removeField(field.key)}
                />
              ))}
            </div>
          )}
        </section>

        <section className="directory-field-panel directory-field-panel--add">
          <h3 className="card__title">Add custom field</h3>
          <div className="stack">
            <Field label="Label">
              <input className="input" value={draft.label} onChange={(event) => setDraft({ ...draft, label: event.target.value })} placeholder="Jersey size" />
            </Field>
            <div className="grid grid--2">
              <Field label="Key">
                <input className="input" value={draft.key} onChange={(event) => setDraft({ ...draft, key: event.target.value })} placeholder={derivedKey || 'jerseySize'} />
              </Field>
              <Field label="Type">
                <select className="select" value={draft.type} onChange={(event) => setDraft({ ...draft, type: event.target.value as LeagueDirectoryField['type'] })}>
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="select">Select</option>
                  <option value="checkbox">Checkbox</option>
                </select>
              </Field>
            </div>
            {draft.type === 'select' && (
              <Field label="Options">
                <input className="input" value={draft.options} onChange={(event) => setDraft({ ...draft, options: event.target.value })} placeholder="Small, Medium, Large" />
              </Field>
            )}
            <div className="directory-field-toggles">
              <label className="chip">
                <input type="checkbox" checked={draft.showInTable} onChange={(event) => setDraft({ ...draft, showInTable: event.target.checked })} />
                Table
              </label>
              <label className="chip">
                <input type="checkbox" checked={draft.registrationField} onChange={(event) => setDraft({ ...draft, registrationField: event.target.checked })} />
                Registration
              </label>
            </div>
            <div className="row row--between" style={{ gap: 12 }}>
              <span className={keyTaken ? 'field__error' : 'field__hint'}>
                {derivedKey ? `Key: ${derivedKey}${keyTaken ? ' is already used' : ''}` : 'Key is created from the label.'}
              </span>
              <button className="btn btn--primary btn--sm" onClick={addField} disabled={!draft.label.trim() || keyTaken}>
                <Plus size={13} /> Add field
              </button>
            </div>
          </div>
        </section>
      </div>
    </Modal>
  );
}

function ManagedFieldRow({
  builtIn,
  field,
  onChange,
  onRemove,
}: {
  builtIn?: boolean;
  field: LeagueDirectoryField;
  onChange: (patch: Partial<LeagueDirectoryField>) => void;
  onRemove?: () => void;
}) {
  return (
    <div className="directory-managed-field">
      <div className="directory-managed-field__main">
        <input
          className="input"
          value={field.label}
          onChange={(event) => onChange({ label: event.target.value })}
          disabled={builtIn}
          aria-label={`${field.label} label`}
        />
        <div className="muted" style={{ fontSize: 12 }}>{field.key} - {field.type}</div>
      </div>
      <select
        className="select"
        value={field.type}
        onChange={(event) => onChange({ type: event.target.value as LeagueDirectoryField['type'] })}
        disabled={builtIn}
        aria-label={`${field.label} type`}
      >
        <option value="text">Text</option>
        <option value="number">Number</option>
        <option value="date">Date</option>
        <option value="select">Select</option>
        <option value="checkbox">Checkbox</option>
      </select>
      <div className="directory-managed-field__options">
        {field.type === 'select' && !builtIn ? (
          <input
            className="input"
            value={(field.options ?? []).join(', ')}
            onChange={(event) => onChange({ options: splitCsv(event.target.value) })}
            placeholder="Options"
            aria-label={`${field.label} options`}
          />
        ) : field.type === 'select' ? (
          <span className="muted" style={{ fontSize: 12 }}>{(field.options ?? []).join(', ') || 'No options'}</span>
        ) : (
          <span className="muted" style={{ fontSize: 12 }}>No options</span>
        )}
      </div>
      <div className="directory-field-toggles">
        <label className="chip">
          <input type="checkbox" checked={Boolean(field.showInTable)} onChange={(event) => onChange({ showInTable: event.target.checked })} />
          Table
        </label>
        <label className="chip">
          <input type="checkbox" checked={Boolean(field.registrationField)} onChange={(event) => onChange({ registrationField: event.target.checked })} />
          Registration
        </label>
      </div>
      <div className="directory-managed-field__actions">
        {builtIn ? (
          <span className="chip">Default</span>
        ) : (
          <button className="btn btn--ghost btn--sm" onClick={onRemove}>
            <X size={13} /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

function ProfileModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form, setForm] = useState<UpsertProfileInput>(() => initialProfile());

  useEffect(() => {
    if (open) setForm(initialProfile());
  }, [open]);

  const emailValid = isValidEmailInput(form.email ?? '');
  const phoneValid = isValidPhoneInput(form.phone ?? '');
  const rolesValid = Boolean(form.globalRoles?.length);
  const canSubmit = Boolean(form.displayName.trim()) && emailValid && phoneValid && rolesValid;

  const onSubmit = () => {
    if (!canSubmit) return;
    const phone = cleanedPhone(form.phone ?? '');
    const roles: Role[] = form.globalRoles?.length ? form.globalRoles : ['player'];
    createProfile({
      ...form,
      email: cleanedEmail(form.email ?? ''),
      phone,
      defaultRole: primaryRole(roles, form.defaultRole ?? 'player'),
      globalRoles: roles,
      contactPreferences: form.contactPreferences ?? {},
    });
    onClose();
  };

  const toggleRole = (role: Role, on: boolean) => {
    const next = new Set(form.globalRoles ?? []);
    if (on) next.add(role);
    else next.delete(role);
    setForm({ ...form, globalRoles: Array.from(next) });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add person"
      subtitle="Add the person once, then attach roles, attributes, teams, and communication preferences over time."
      size="lg"
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={onSubmit} disabled={!canSubmit}>Add person</button>
        </>
      }
    >
      <div className="stack" style={{ marginTop: 16 }}>
        <Field label="Display name">
          <input className="input" value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} />
        </Field>
        <div className="grid grid--2">
          <Field label="Email / Google OAuth identity">
            <input className="input" type="email" autoComplete="email" value={form.email ?? ''} onChange={(event) => setForm({ ...form, email: cleanEmailInput(event.target.value) })} aria-invalid={!emailValid} />
            {!emailValid && <span className="field__error">Enter a valid email address.</span>}
          </Field>
          <Field label="Phone">
            <input className="input" type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 123-4567" value={form.phone ?? ''} onChange={(event) => setForm({ ...form, phone: cleanPhoneInput(event.target.value) })} aria-invalid={!phoneValid} />
            {!phoneValid && <span className="field__error">Enter 10 to 15 digits.</span>}
          </Field>
        </div>
        <Field label="Roles">
          <div className="row" style={{ gap: 6 }}>
            {ALL_ROLES.map((role) => {
              const on = (form.globalRoles ?? []).includes(role);
              return (
                <label key={role} className={`chip ${on ? 'chip--accent' : ''}`} style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={on} onChange={(event) => toggleRole(role, event.target.checked)} style={{ marginRight: 6 }} />
                  {ROLE_LABEL[role]}
                </label>
              );
            })}
          </div>
          {!rolesValid && <span className="field__error">Select at least one role.</span>}
        </Field>
        <div className="directory-consent-row directory-consent-row--readonly">
          <Phone size={16} />
          <span>
            <strong>Text consent</strong>
            <span>Text opt-ins are collected during registration. Admins cannot manually grant SMS or marketing consent.</span>
          </span>
        </div>
      </div>
    </Modal>
  );
}

function CompactMetric({ icon: Icon, label, value, tone }: { icon: LucideIcon; label: string; value: number; tone?: 'warn' }) {
  return (
    <div className={`directory-metric ${tone === 'warn' ? 'is-warn' : ''}`}>
      <Icon size={15} />
      <div>
        <strong>{value}</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange }: { label: string; value: string; options: Array<[string, string]>; onChange: (value: string) => void }) {
  return (
    <label className="field directory-filter">
      <span className="field__label">{label}</span>
      <select className="select" value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </label>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
    </label>
  );
}

function getAccessLabel(profile: Profile): { label: string; tone: 'ok' | 'info' | 'warn' } {
  if (profile.directoryStatus === 'archived') return { label: 'Archived', tone: 'info' };
  if (profile.directoryStatus === 'inactive') return { label: 'Inactive', tone: 'warn' };
  if (profile.globalRoles.includes('admin')) return { label: 'League HQ admin', tone: 'ok' };
  if (profile.email) return { label: 'Member sign-in', tone: 'info' };
  return { label: 'No sign-in email', tone: 'warn' };
}

function formatAttribute(value: unknown): string {
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function normalizeFieldKey(value: string): string {
  return value
    .trim()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr: string) => chr.toUpperCase())
    .replace(/^[^a-zA-Z]+/, '')
    .replace(/^([A-Z])/, (letter) => letter.toLowerCase());
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function cleanEmailInput(value: string): string {
  return value.replace(/\s+/g, '').toLowerCase();
}

function cleanedEmail(value: string): string | undefined {
  return cleanEmailInput(value).trim() || undefined;
}

function isValidEmailInput(value: string): boolean {
  const email = cleanedEmail(value);
  return !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function cleanPhoneInput(value: string): string {
  const trimmed = value.trim();
  const prefix = trimmed.startsWith('+') ? '+' : '';
  return `${prefix}${trimmed.replace(/\D+/g, '').slice(0, 15)}`;
}

function cleanedPhone(value: string): string | undefined {
  return cleanPhoneInput(value) || undefined;
}

function isValidPhoneInput(value: string): boolean {
  const digits = cleanPhoneInput(value).replace(/\D+/g, '');
  return digits.length === 0 || (digits.length >= 10 && digits.length <= 15);
}

function primaryRole(roles: Role[], fallback: Role): Role {
  return roles.includes(fallback) ? fallback : roles[0] ?? fallback;
}

function initialProfile(): UpsertProfileInput {
  return {
    displayName: '',
    email: '',
    phone: '',
    defaultRole: 'player',
    globalRoles: ['player'],
    preferredContactMethod: 'email',
    directoryStatus: 'active',
    contactPreferences: {},
    attributes: {},
    source: 'manual',
    tags: [],
  };
}

function splitCsv(value: string): string[] {
  return value.split(',').map((item) => item.trim()).filter(Boolean);
}
